import * as THREE from 'three';
import type { DuckNetState } from '../../shared/protocol';

export type DuckKind = 'standard' | 'kamikaze' | 'armored' | 'medic' | 'sniper' | 'boss';

export type DuckProfile = {
  label: string;
  maxHealth: number;
  damage: number;
  speed: number;
  attackRange: number;
  attackSeconds: number;
  preferredDistance: number;
  color: number;
  accent: number;
  scale: number;
};

export type DuckModifiers = {
  health?: number;
  damage?: number;
  speed?: number;
};

export const DUCK_PROFILES: Record<DuckKind, DuckProfile> = {
  standard: {
    label: 'Paperella operaia', maxHealth: 72, damage: 10, speed: 1.9,
    attackRange: 1.55, attackSeconds: 1.35, preferredDistance: 1.15,
    color: 0xf5cc28, accent: 0xe8a91d, scale: 1,
  },
  kamikaze: {
    label: 'Paperella stappatrice', maxHealth: 44, damage: 34, speed: 3.05,
    attackRange: 2.05, attackSeconds: 2.4, preferredDistance: 1.2,
    color: 0xf05b32, accent: 0xffb126, scale: 0.9,
  },
  armored: {
    label: 'Paperella corazzata', maxHealth: 185, damage: 18, speed: 1.15,
    attackRange: 1.75, attackSeconds: 1.75, preferredDistance: 1.25,
    color: 0xd3a52a, accent: 0x53656a, scale: 1.22,
  },
  medic: {
    label: 'Paperella infermiera', maxHealth: 88, damage: 7, speed: 1.7,
    attackRange: 1.5, attackSeconds: 1.8, preferredDistance: 1.1,
    color: 0xa9e87c, accent: 0x55c9c1, scale: 0.98,
  },
  sniper: {
    label: 'Paperella sparabolle', maxHealth: 64, damage: 16, speed: 1.35,
    attackRange: 17, attackSeconds: 2.65, preferredDistance: 10.5,
    color: 0x8f72d8, accent: 0x65dbea, scale: 1.02,
  },
  boss: {
    label: 'Quackodemon', maxHealth: 760, damage: 28, speed: 1.38,
    attackRange: 8.5, attackSeconds: 1.25, preferredDistance: 4.8,
    color: 0x8d42a8, accent: 0xf06a2b, scale: 1.9,
  },
};

const hashId = (id: string): number => {
  let hash = 2166136261;
  for (const char of id) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
  return hash >>> 0;
};

const material = (color: number, options: Partial<THREE.MeshStandardMaterialParameters> = {}): THREE.MeshStandardMaterial =>
  new THREE.MeshStandardMaterial({ color, roughness: 0.42, metalness: 0.04, flatShading: true, ...options });

export const createDuckBillGeometry = (width: number, height: number, length: number): THREE.BufferGeometry => {
  const backX = width / 2;
  const backY = height / 2;
  const frontX = width * 0.38;
  const frontY = height * 0.28;
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute([
    -backX, -backY, 0, backX, -backY, 0, -backX, backY, 0, backX, backY, 0,
    -frontX, -frontY, length, frontX, -frontY, length, -frontX, frontY, length, frontX, frontY, length,
  ], 3));
  geometry.setIndex([
    0, 2, 3, 0, 3, 1, 4, 5, 7, 4, 7, 6, 2, 6, 7, 2, 7, 3,
    0, 1, 5, 0, 5, 4, 0, 4, 6, 0, 6, 2, 1, 3, 7, 1, 7, 5,
  ]);
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  return geometry;
};

export class Duck {
  readonly id: string;
  readonly kind: DuckKind;
  readonly profile: DuckProfile;
  readonly group = new THREE.Group();
  readonly maxHealth: number;
  readonly navigationBias: number;
  readonly laneOffset: number;
  health: number;
  alive = true;
  attackCooldown = 0;
  abilityCooldown = 0;
  private readonly baseY: number;
  private readonly moveDirection = new THREE.Vector3();
  private readonly moveAttemptDirection = new THREE.Vector3();
  private readonly moveAttempt = new THREE.Vector3();
  private readonly upAxis = new THREE.Vector3(0, 1, 0);
  private readonly avoidanceAngles: readonly number[];
  private readonly wings: Array<{ mesh: THREE.Mesh; side: number }> = [];
  private readonly feet: Array<{ mesh: THREE.Mesh; side: number }> = [];
  private readonly animatedSignals: THREE.MeshStandardMaterial[] = [];
  private readonly detailMeshes: THREE.Object3D[] = [];
  private lowerBeak?: THREE.Mesh;
  private head?: THREE.Mesh;
  private body?: THREE.Mesh;
  private hitFlash = 0;
  private attackPulse = 0;
  private deathProgress = 0;
  private moving = false;
  private detailsVisible = true;
  private rubber: THREE.MeshStandardMaterial;
  private readonly damageScale: number;
  private readonly speedScale: number;

  constructor(id: string, kind: DuckKind, position: THREE.Vector3, modifiers: DuckModifiers = {}) {
    this.id = id;
    this.kind = kind;
    this.profile = DUCK_PROFILES[kind];
    this.maxHealth = Math.round(this.profile.maxHealth * (modifiers.health ?? 1));
    this.health = this.maxHealth;
    this.damageScale = modifiers.damage ?? 1;
    this.speedScale = modifiers.speed ?? 1;
    const hash = hashId(id);
    this.navigationBias = hash % 2 === 0 ? 1 : -1;
    this.laneOffset = ((hash % 7) - 3) * 0.13;
    this.avoidanceAngles = [0, this.navigationBias * 0.58, -this.navigationBias * 0.58, this.navigationBias * 1.1, -this.navigationBias * 1.1];
    this.baseY = kind === 'boss' ? 1.35 : kind === 'armored' ? 0.84 : 0.7;
    this.rubber = material(this.profile.color, { roughness: 0.3 });
    this.group.position.copy(position);
    this.group.position.y = this.baseY;
    this.buildModel();
    this.group.traverse((node) => { node.userData.duckId = id; });
  }

  get damage(): number { return Math.round(this.profile.damage * this.damageScale); }
  get speed(): number { return this.profile.speed * this.speedScale; }
  get attackRange(): number { return this.profile.attackRange; }
  get attackSeconds(): number { return this.profile.attackSeconds; }
  get preferredDistance(): number { return this.profile.preferredDistance; }

  hit(amount: number): boolean {
    if (!this.alive) return false;
    const mitigated = this.kind === 'armored' ? Math.max(1, amount * 0.62) : amount;
    this.health = Math.max(0, this.health - mitigated);
    this.hitFlash = 0.16;
    if (this.health <= 0) {
      this.alive = false;
      this.deathProgress = 0;
      return true;
    }
    return false;
  }

  heal(amount: number): boolean {
    if (!this.alive || this.health >= this.maxHealth) return false;
    this.health = Math.min(this.maxHealth, this.health + amount);
    for (const signal of this.animatedSignals) signal.emissiveIntensity = 1.4;
    return true;
  }

  beginAttack(): void {
    this.attackCooldown = this.attackSeconds;
    this.attackPulse = 1;
  }

  setDetailVisible(visible: boolean): void {
    if (this.kind === 'boss' || this.detailsVisible === visible) return;
    this.detailsVisible = visible;
    for (const detail of this.detailMeshes) detail.visible = visible;
  }

  updateVisual(time: number, dt: number): void {
    this.attackCooldown = Math.max(0, this.attackCooldown - dt);
    this.abilityCooldown = Math.max(0, this.abilityCooldown - dt);
    this.hitFlash = Math.max(0, this.hitFlash - dt);
    this.attackPulse = Math.max(0, this.attackPulse - dt * 4.5);
    this.rubber.emissive.setHex(this.hitFlash > 0 ? 0xffffff : 0x000000);
    this.rubber.emissiveIntensity = this.hitFlash > 0 ? 0.9 : 0;

    if (!this.alive) {
      this.deathProgress = Math.min(1.2, this.deathProgress + dt);
      const squash = Math.min(1, this.deathProgress / 0.52);
      const baseScale = this.profile.scale;
      this.group.scale.set(
        baseScale * (1 + squash * 0.28),
        baseScale * Math.max(0.055, 1 - squash * 0.94),
        baseScale * (1 + squash * 0.2),
      );
      this.group.rotation.z += dt * (2.2 + squash * 2.8) * this.navigationBias;
      this.group.position.y = THREE.MathUtils.lerp(this.baseY, 0.12, squash);
      if (this.deathProgress >= 1.1) this.group.visible = false;
      return;
    }

    if (!this.group.visible) return;
    const stride = this.moving ? (this.kind === 'kamikaze' ? 13 : 8.5) : 4.5;
    const bob = this.moving ? 0.085 : 0.045;
    const phase = time * stride + hashId(this.id) * 0.001;
    this.group.position.y = this.baseY + Math.abs(Math.sin(phase)) * bob;
    this.group.rotation.z = Math.sin(phase) * (this.moving ? 0.055 : 0.025) + this.hitFlash * this.navigationBias;
    if (this.body) this.body.rotation.z = Math.sin(phase) * (this.moving ? 0.045 : 0.018);
    if (this.head) {
      this.head.rotation.x = -this.attackPulse * 0.22 + Math.sin(phase * 0.5) * 0.025;
      this.head.position.z = 0.34 + this.attackPulse * 0.14;
    }
    const flap = 0.08 + Math.sin(phase * 1.15) * (this.moving ? 0.16 : 0.07) + this.attackPulse * 0.22;
    for (const wing of this.wings) wing.mesh.rotation.z = wing.side * (-1.16 - flap);
    for (const foot of this.feet) foot.mesh.rotation.x = Math.sin(phase + foot.side * Math.PI / 2) * (this.moving ? 0.38 : 0.08);
    if (this.lowerBeak) this.lowerBeak.rotation.x = 0.035 + this.attackPulse * 0.24;
    for (const signal of this.animatedSignals) {
      const warning = this.kind === 'kamikaze' && this.attackCooldown < 0.7;
      signal.emissiveIntensity = warning ? (Math.sin(time * 28) > 0 ? 2.4 : 0.25) : 0.48 + Math.sin(time * 5) * 0.2;
    }
    this.moving = false;
  }

  faceAndMoveToward(
    target: THREE.Vector3,
    dt: number,
    canMove: (next: THREE.Vector3) => boolean,
    stopDistance = this.preferredDistance,
  ): boolean {
    if (!this.alive) return false;
    const direction = this.moveDirection.set(target.x - this.group.position.x, 0, target.z - this.group.position.z);
    const distance = direction.length();
    if (distance > 0.001) {
      direction.normalize();
      this.group.rotation.y = Math.atan2(direction.x, direction.z);
    }
    if (distance > stopDistance) {
      for (const angle of this.avoidanceAngles) {
        const attemptDirection = this.moveAttemptDirection.copy(direction).applyAxisAngle(this.upAxis, angle);
        const next = this.moveAttempt.copy(this.group.position).addScaledVector(attemptDirection, this.speed * dt);
        next.y = this.baseY;
        if (!canMove(next)) continue;
        this.group.position.copy(next);
        this.moving = true;
        return true;
      }
    }
    return false;
  }

  toSnapshot(): DuckNetState {
    return {
      id: this.id,
      x: this.group.position.x,
      z: this.group.position.z,
      rotation: this.group.rotation.y,
      health: this.health,
      alive: this.alive,
    };
  }

  applySnapshot(snapshot: DuckNetState): void {
    this.group.position.x = THREE.MathUtils.lerp(this.group.position.x, snapshot.x, 0.55);
    this.group.position.z = THREE.MathUtils.lerp(this.group.position.z, snapshot.z, 0.55);
    this.group.rotation.y = snapshot.rotation;
    this.health = snapshot.health;
    if (this.alive && !snapshot.alive) this.hit(this.health || this.maxHealth);
    this.alive = snapshot.alive;
  }

  private buildModel(): void {
    const dark = material(0x182225, { metalness: 0.68, roughness: 0.48 });
    const metal = material(this.profile.accent, { metalness: 0.46, roughness: 0.36 });
    const bellyMaterial = material(this.kind === 'medic' ? 0xe8fff1 : this.kind === 'sniper' ? 0xd9cef5 : 0xffdf56, { roughness: 0.48 });
    const wingMaterial = material(new THREE.Color(this.profile.color).offsetHSL(0, -0.04, -0.07).getHex(), { roughness: 0.35 });
    const porcelain = material(0xe5f4ef, { roughness: 0.24 });
    const orange = material(0xf47724, { roughness: 0.3 });
    const signal = material(this.profile.accent, { emissive: this.profile.accent, emissiveIntensity: 0.55, roughness: 0.24 });
    this.animatedSignals.push(signal);

    const body = new THREE.Mesh(new THREE.DodecahedronGeometry(0.76, 1), this.rubber);
    body.scale.set(1.3, 0.86, 1.06);
    body.castShadow = true;
    this.body = body;
    this.group.add(body);

    const belly = new THREE.Mesh(new THREE.DodecahedronGeometry(0.46, 0), bellyMaterial);
    belly.position.set(0, -0.09, 0.5);
    belly.scale.set(1.05, 0.74, 0.38);
    this.group.add(belly);

    const head = new THREE.Mesh(new THREE.IcosahedronGeometry(0.56, 1), this.rubber);
    head.position.set(0, 0.68, 0.34);
    head.scale.set(1.03, 1.04, 0.98);
    this.head = head;
    this.group.add(head);

    const wingGeometry = new THREE.ConeGeometry(0.38, 0.72, 5);
    for (const side of [-1, 1]) {
      const wing = new THREE.Mesh(wingGeometry, wingMaterial);
      wing.position.set(side * 0.74, 0.05, 0.08);
      wing.scale.set(0.72, 1.05, 0.55);
      wing.rotation.z = side * -1.18;
      wing.rotation.x = -0.22;
      this.group.add(wing);
      this.wings.push({ mesh: wing, side });

      const foot = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.09, 0.46), orange);
      foot.position.set(side * 0.32, -0.61, 0.18);
      foot.rotation.y = side * 0.08;
      this.group.add(foot);
      this.feet.push({ mesh: foot, side });
    }

    const beak = new THREE.Mesh(createDuckBillGeometry(0.72, 0.17, 0.5), orange);
    beak.position.set(0, 0.58, 0.72);
    this.group.add(beak);
    const lowerBeak = new THREE.Mesh(createDuckBillGeometry(0.64, 0.12, 0.44), material(0xc9481c, { roughness: 0.4 }));
    lowerBeak.position.set(0, 0.5, 0.74);
    lowerBeak.rotation.x = 0.035;
    this.group.add(lowerBeak);
    this.lowerBeak = lowerBeak;

    const eyeWhite = material(0xf9f6e7, { roughness: 0.82 });
    const pupil = material(0x111111, { roughness: 1 });
    for (const side of [-1, 1]) {
      const eye = new THREE.Mesh(new THREE.IcosahedronGeometry(0.145, 1), eyeWhite);
      eye.position.set(side * 0.21, 0.83, 0.78);
      eye.scale.set(1.05, 1.08, 0.58);
      const dot = new THREE.Mesh(new THREE.IcosahedronGeometry(0.068, 1), pupil);
      dot.position.set(side * 0.2, 0.82, 0.91);
      dot.scale.z = 0.35;
      const brow = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.08, 0.09), dark);
      brow.position.set(side * 0.2, 1.01, 0.84);
      brow.rotation.z = side * (this.kind === 'medic' ? 0.12 : 0.34);
      this.group.add(eye, dot, brow);
    }

    const collar = new THREE.Mesh(new THREE.TorusGeometry(0.72, 0.085, 5, 12), dark);
    collar.position.set(0, 0.21, -0.03);
    collar.rotation.x = Math.PI / 2;
    collar.scale.z = 0.88;
    const badge = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.07, 8), signal);
    badge.position.set(0, 0.08, 0.79);
    badge.rotation.x = Math.PI / 2;
    this.group.add(collar, badge);

    this.addVariantDetails({ dark, metal, porcelain, orange, signal });
    const essentials = new Set<THREE.Object3D>([body, head, beak, lowerBeak, ...this.wings.map((wing) => wing.mesh)]);
    this.detailMeshes.push(...this.group.children.filter((child) => !essentials.has(child)));
    this.group.scale.setScalar(this.profile.scale);
  }

  private addVariantDetails(materials: Record<'dark' | 'metal' | 'porcelain' | 'orange' | 'signal', THREE.MeshStandardMaterial>): void {
    const { dark, metal, porcelain, orange, signal } = materials;
    if (this.kind === 'kamikaze') {
      const tank = new THREE.Mesh(new THREE.CylinderGeometry(0.25, 0.25, 0.72, 8), dark);
      tank.position.set(0, 0.2, -0.68);
      tank.rotation.z = Math.PI / 2;
      const cap = new THREE.Mesh(new THREE.IcosahedronGeometry(0.18, 0), signal);
      cap.position.set(0, 0.55, -0.58);
      const fuse = new THREE.Mesh(new THREE.TorusGeometry(0.21, 0.045, 5, 8, Math.PI), orange);
      fuse.position.set(0.05, 0.67, -0.55);
      fuse.rotation.z = -0.4;
      this.group.add(tank, cap, fuse);
    } else if (this.kind === 'armored') {
      const shell = new THREE.Mesh(new THREE.DodecahedronGeometry(0.81, 0), metal);
      shell.position.set(0, 0.02, -0.02);
      shell.scale.set(1.34, 0.82, 0.9);
      const faceGuard = new THREE.Mesh(new THREE.BoxGeometry(1.05, 0.16, 0.12), dark);
      faceGuard.position.set(0, 0.79, 0.96);
      const rivetGeometry = new THREE.IcosahedronGeometry(0.07, 0);
      for (const side of [-1, 1]) {
        const shoulder = new THREE.Mesh(new THREE.DodecahedronGeometry(0.31, 0), metal);
        shoulder.position.set(side * 0.78, 0.26, 0.1);
        const rivet = new THREE.Mesh(rivetGeometry, orange);
        rivet.position.set(side * 0.78, 0.28, 0.39);
        this.group.add(shoulder, rivet);
      }
      this.group.add(shell, faceGuard);
    } else if (this.kind === 'medic') {
      const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.34, 0.44, 0.18, 8), porcelain);
      cap.position.set(0, 1.16, 0.28);
      const crossH = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.1, 0.06), signal);
      const crossV = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.32, 0.06), signal);
      crossH.position.set(0, 1.17, 0.63);
      crossV.position.copy(crossH.position);
      const soapTank = new THREE.Mesh(new THREE.CylinderGeometry(0.17, 0.17, 0.64, 8), signal);
      soapTank.position.set(0, 0.28, -0.72);
      soapTank.rotation.z = Math.PI / 2;
      this.group.add(cap, crossH, crossV, soapTank);
    } else if (this.kind === 'sniper') {
      const cannon = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.15, 0.92, 8), dark);
      cannon.position.set(0.43, 0.43, 0.25);
      cannon.rotation.x = Math.PI / 2;
      const bubble = new THREE.Mesh(new THREE.IcosahedronGeometry(0.2, 1), signal);
      bubble.position.set(0.43, 0.43, 0.82);
      const scope = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.11, 0.34, 8), signal);
      scope.position.set(0, 0.95, 0.38);
      scope.rotation.x = Math.PI / 2;
      this.group.add(cannon, bubble, scope);
    } else if (this.kind === 'boss') {
      const hornGeometry = new THREE.ConeGeometry(0.18, 0.7, 5);
      for (const side of [-1, 1]) {
        const horn = new THREE.Mesh(hornGeometry, orange);
        horn.position.set(side * 0.42, 1.34, 0.14);
        horn.rotation.z = side * -0.52;
        const tank = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.2, 0.86, 8), dark);
        tank.position.set(side * 0.43, 0.35, -0.72);
        this.group.add(horn, tank);
      }
      const halo = new THREE.Mesh(new THREE.TorusGeometry(0.86, 0.1, 5, 14), signal);
      halo.position.set(0, 0.2, -0.18);
      halo.rotation.x = Math.PI / 2;
      const crest = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.38, 0.5, 5), metal);
      crest.position.set(0, 1.38, 0.2);
      crest.rotation.y = Math.PI / 5;
      this.group.add(halo, crest);
    } else {
      const pressureTank = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.13, 0.48, 8), dark);
      pressureTank.position.set(-0.28, 0.26, -0.7);
      pressureTank.rotation.z = Math.PI / 2;
      const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.16, 0.09, 8), metal);
      cap.position.set(-0.55, 0.26, -0.7);
      cap.rotation.z = Math.PI / 2;
      this.group.add(pressureTank, cap);
    }
  }
}
